import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { User, UserRole } from 'types';
import apiReq from 'core/api';
import { tokenStorage } from 'utils';
import { cursorStorage } from 'services/broadcastPolling';

// ─── Backend response types ───────────────────────────────────────────────────
interface BackendLoginResponse {
  access_token: string;
}

interface BackendSwitchResponse {
  id: string;
  access_token: string;
}

interface BackendRole {
  id?: string | number;
  name: string;
  title: string;
}

interface BackendUser {
  id?: string | number;
  name: string;
  email: string;
  roles: BackendRole[];
  is_admin: boolean;
  is_active: boolean;
  is_demo?: boolean;
}

// ─── Role switch emails ───────────────────────────────────────────────────────
const ROLE_EMAILS: Record<UserRole, string> = {
  admin: 'admin@example.com',
  hr: 'herman@example.com',
  lawyer: 'juri@example.com',
  ops: 'peter@example.com',
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
function mapRole(backendUser: BackendUser): UserRole {
  if (backendUser.is_admin) return 'admin';
  const name = (backendUser.roles?.[0]?.name ?? '').toLowerCase();
  if (name.includes('hr')) return 'hr';
  if (name.includes('law') || name.includes('юрист') || name.includes('juri')) return 'lawyer';
  if (name.includes('ops') || name.includes('oper') || name.includes('peter')) return 'ops';
  return 'lawyer';
}

function getInitials(name: string | null | undefined): string {
  return (name ?? '')
    .split(' ')
    .slice(0, 2)
    .map((w) => w[0] ?? '')
    .join('')
    .toUpperCase();
}

async function doLogin(
  email: string,
  password: string
): Promise<{ user: User; token: string } | null> {
  try {
    const loginData = await apiReq<BackendLoginResponse>('auth/login', 'POST', { email, password });
    if (!loginData?.access_token) return null;

    tokenStorage.set(loginData.access_token);

    const backendUser = await apiReq<BackendUser>('auth/info');
    if (!backendUser) return null;

    const role = mapRole(backendUser);

    let projectId = '';
    try {
      const projectsData = await apiReq<Array<{ id: string }>>('projects');
      projectId = projectsData?.[0]?.id ?? '';
    } catch {
      // non-critical
    }

    const roleId = String(backendUser.roles?.[0]?.id ?? '');

    const user: User = {
      id: String(backendUser.id ?? email),
      name: backendUser.name,
      email: backendUser.email,
      role,
      avatarInitials: getInitials(backendUser.name),
      projectId,
      roleId,
      isDemo: backendUser.is_demo ?? false,
    };

    return { user, token: loginData.access_token };
  } catch {
    tokenStorage.remove();
    return null;
  }
}

// ─── Store ────────────────────────────────────────────────────────────────────
interface AuthState {
  user: User | null;
  token: string | null;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
  switchTo: (role: UserRole) => Promise<void>;
  refreshUser: () => Promise<void>;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,

      login: async (email, password) => {
        const result = await doLogin(email, password);
        if (result) {
          set({ user: result.user, token: result.token });
          return true;
        }
        return false;
      },

      logout: () => {
        // Чистим cursor polling до сброса user — иначе userId уже недоступен.
        const currentUserId = get().user?.id;
        if (currentUserId) cursorStorage.clear(currentUserId);
        tokenStorage.remove();
        set({ user: null, token: null });
      },

      switchTo: async (role) => {
        const email = ROLE_EMAILS[role];
        if (!email) return;
        try {
          const switchData = await apiReq<BackendSwitchResponse>('auth/switch', 'POST', { email });
          if (!switchData?.access_token) return;

          tokenStorage.set(switchData.access_token);

          const backendUser = await apiReq<BackendUser>('auth/info');
          if (!backendUser) return;

          const mappedRole = mapRole(backendUser);
          const roleId = String(backendUser.roles?.[0]?.id ?? '');

          let projectId = '';
          try {
            const projectsData = await apiReq<Array<{ id: string }>>('projects');
            projectId = projectsData?.[0]?.id ?? '';
          } catch {
            // non-critical
          }

          const user: User = {
            id: switchData.id,
            name: backendUser.name,
            email: backendUser.email,
            role: mappedRole,
            avatarInitials: getInitials(backendUser.name),
            projectId,
            roleId,
            isDemo: backendUser.is_demo ?? false,
          };

          set({ user, token: switchData.access_token });
        } catch {
          tokenStorage.remove();
        }
      },

      refreshUser: async () => {
        try {
          const backendUser = await apiReq<BackendUser>('auth/info');
          if (!backendUser) return;

          const role = mapRole(backendUser);
          const roleId = String(backendUser.roles?.[0]?.id ?? '');

          let projectId = '';
          try {
            const projectsData = await apiReq<Array<{ id: string }>>('projects');
            projectId = projectsData?.[0]?.id ?? '';
          } catch {
            /* non-critical */
          }

          set((state) => ({
            user: state.user
              ? {
                  ...state.user,
                  role,
                  roleId,
                  projectId: projectId || state.user.projectId,
                  name: backendUser.name,
                  email: backendUser.email,
                  avatarInitials: getInitials(backendUser.name),
                  isDemo: backendUser.is_demo ?? false,
                }
              : null,
          }));
        } catch {
          /* non-critical */
        }
      },
    }),
    { name: 'absolut-auth' }
  )
);
